import { TTelemetryLogger } from 'shared/types/telemetry';

/**
 * Метрики показов продвигаемых объявлений для алертов в Grafana.
 *
 * Модель: счётчик именуется по ПРИЗНАКУ отображения продвижения, а факт продвижения (CPC или CPT)
 * — это условие инкремента. Счётчик += 1, когда объявление продвигается (cpc || cpt) И данный признак
 * РЕАЛЬНО отрендерился (честная метрика — предикаты считаются из тех же computed-пропсов, что и рендер,
 * а не из сырых флагов оффера).
 *
 * Платформа «веб-выдача» закодирована в пути Graphite сегментом `frontend-serp` (моб. веб — отдельный
 * сервис/репозиторий), поэтому в имя метрики не дублируется.
 *
 * У @cian/telemetry нет лейблов/тегов — каждый признак это отдельное имя метрики (dot-notation).
 */
export const PROMO_OFFER_SHOW_TELEMETRY = {
  /** Любой показ продвигаемого (CPC/CPT) объявления — знаменатель для долей (photo/title/description). */
  shown: 'promo-offer-show.shown',
  /** Продвигаемое (CPC/CPT) объявление показано с большой галереей — миниатюры реально отрисованы. */
  photo: 'promo-offer-show.photo',
  /** Продвигаемое (CPC/CPT) объявление показано со спец. промо-заголовком в фирменном стиле. */
  title: 'promo-offer-show.title',
  /** Продвигаемое (CPC/CPT) объявление показано с блоком описания продвижения (promotionDescription). */
  description: 'promo-offer-show.description',
} as const;

interface ITrackPromotedOfferShowParams {
  /** Реально отрисована большая галерея с миниатюрами (4 фото в выдаче). */
  hasPromoPhoto: boolean;
  /** Реально отрисован промо-заголовок в спец-стиле. */
  hasPromoTitle: boolean;
  /** Реально отрисован блок описания продвижения. */
  hasPromoDescription: boolean;
}

/**
 * Инкрементит метрики показа продвигаемого объявления по каждому реально отрисованному признаку.
 *
 * Вызывается один раз на карточку при попадании во вьюпорт (см. OfferCardContainer) И только для
 * продвигаемого объявления (cpc || cpt) — само это условие проверяется на стороне вызова.
 */
export function trackPromotedOfferShowTelemetry(
  telemetry: TTelemetryLogger,
  { hasPromoPhoto, hasPromoTitle, hasPromoDescription }: ITrackPromotedOfferShowParams,
): void {
  // Знаменатель: любой показ продвигаемого объявления (функция вызывается только для cpc || cpt).
  telemetry.increment(PROMO_OFFER_SHOW_TELEMETRY.shown);

  if (hasPromoPhoto) {
    telemetry.increment(PROMO_OFFER_SHOW_TELEMETRY.photo);
  }

  if (hasPromoTitle) {
    telemetry.increment(PROMO_OFFER_SHOW_TELEMETRY.title);
  }

  if (hasPromoDescription) {
    telemetry.increment(PROMO_OFFER_SHOW_TELEMETRY.description);
  }
}
